import { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { createAdminTenant } from '@/modules/admin/services/admin-tenants.service';
import { uploadAdminAsset } from '@/modules/admin/services/admin-assets.service';
import { getRoutePath, ROUTE_IDS } from '@/app/router/route-registry';

const MODULE_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'deals', label: 'Deals' },
  { key: 'clients', label: 'Clients' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'staff', label: 'Staff' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'documents', label: 'Documents' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'settings', label: 'Settings' },
];

const EMPTY_CREATE_FORM = {
  tenantId: '',
  name: '',
  slug: '',
  planCode: 'standard',
  enabledModuleKeys: ['dashboard', 'clients', 'suppliers', 'staff', 'calendar', 'documents', 'deals', 'accounting', 'settings'],
  ownerFullName: '',
  ownerEmail: '',
  ownerPhone: '',
  ownerProfessionalTitle: '',
  ownerTipoPersonale: 'interno',
  ownerSkills: '',
};

const EMPTY_ASSET_FILES = {
  sidebarLogo: null,
  invoiceTemplate: null,
  contractTemplate: null,
  genericTemplate: null,
};

function resolveInviteLink(tenant) {
  const inviteUrl = tenant?.ownerInviteUrl || '';
  const inviteToken = tenant?.ownerInviteToken || tenant?.ownerInvitePreviewToken || '';
  const rawLink = inviteUrl || (inviteToken ? `/accept-invite/${inviteToken}` : '');

  if (!rawLink) {
    return '';
  }

  if (/^https?:\/\//.test(rawLink)) {
    return rawLink;
  }

  if (typeof window === 'undefined' || !window.location?.origin) {
    return rawLink;
  }

  return `${window.location.origin}${rawLink.startsWith('/') ? rawLink : `/${rawLink}`}`;
}

export default function AdminCreateTenantPage() {
  const { refreshAdminMetrics } = useOutletContext() || {};
  const tenantsPath = getRoutePath(ROUTE_IDS.ADMIN_TENANTS) || '/admin/tenants';
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [isCreatingTenant, setIsCreatingTenant] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createdTenant, setCreatedTenant] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [assetFiles, setAssetFiles] = useState(EMPTY_ASSET_FILES);

  const enabledModuleCount = useMemo(
    () => createForm.enabledModuleKeys.length,
    [createForm.enabledModuleKeys]
  );
  const isStaffModuleEnabled = createForm.enabledModuleKeys.includes('staff');
  const validationErrors = useMemo(() => validateCreateTenantPayload({
    tenantId: createForm.tenantId.trim(),
    name: createForm.name.trim(),
    slug: createForm.slug.trim(),
    planCode: createForm.planCode.trim(),
    enabledModuleKeys: createForm.enabledModuleKeys,
    ownerFullName: createForm.ownerFullName.trim(),
    ownerEmail: createForm.ownerEmail.trim(),
    ownerPhone: createForm.ownerPhone.trim(),
    ownerProfessionalTitle: createForm.ownerProfessionalTitle.trim(),
  }), [createForm]);
  const missingFields = useMemo(() => new Set(validationErrors.map((error) => error.field)), [validationErrors]);
  const isCreateDisabled = isCreatingTenant || validationErrors.length > 0;

  function updateCreateForm(field, value) {
    setCreateError('');
    setCreateForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function updateAssetFile(field, file) {
    setCreateError('');
    setAssetFiles((currentFiles) => ({
      ...currentFiles,
      [field]: file || null,
    }));
  }

  async function uploadTenantAssets(createdTenantId) {
    const uploads = [
      assetFiles.sidebarLogo
        ? {
            file: assetFiles.sidebarLogo,
            scope: 'TENANT_BRAND',
            tenantId: createdTenantId,
            usageKey: 'tenant_sidebar_logo',
            displayName: 'Logo sidebar tenant',
            autoAssign: true,
          }
        : null,
      assetFiles.invoiceTemplate
        ? {
            file: assetFiles.invoiceTemplate,
            scope: 'TENANT_TEMPLATE',
            tenantId: createdTenantId,
            usageKey: 'tenant_invoice_template',
            displayName: 'Template fattura',
            autoAssign: true,
          }
        : null,
      assetFiles.contractTemplate
        ? {
            file: assetFiles.contractTemplate,
            scope: 'TENANT_TEMPLATE',
            tenantId: createdTenantId,
            usageKey: 'tenant_contract_template',
            displayName: 'Template contratto',
            autoAssign: true,
          }
        : null,
      assetFiles.genericTemplate
        ? {
            file: assetFiles.genericTemplate,
            scope: 'TENANT_TEMPLATE',
            tenantId: createdTenantId,
            usageKey: 'tenant_generic_document_template',
            displayName: 'Template generico',
            autoAssign: true,
          }
        : null,
    ].filter(Boolean);

    if (uploads.length === 0) {
      return true;
    }

    const results = await Promise.all(uploads.map((upload) => uploadAdminAsset(upload)));
    return results.every((result) => result.ok);
  }

  function toggleModule(moduleKey) {
    setCreateError('');
    setCreateForm((currentForm) => ({
      ...currentForm,
      enabledModuleKeys: currentForm.enabledModuleKeys.includes(moduleKey)
        ? currentForm.enabledModuleKeys.filter((currentModuleKey) => currentModuleKey !== moduleKey)
        : [...currentForm.enabledModuleKeys, moduleKey],
    }));
  }

  function validateCreateTenantPayload(payload) {
    const errors = [];

    if (!payload.tenantId || !payload.name || !payload.slug || !payload.planCode) {
      errors.push({ field: 'tenant', message: 'Compila tenant ID, nome, slug e piano.' });
    }

    if (!payload.ownerFullName || !payload.ownerEmail) {
      errors.push({ field: 'owner', message: "Compila nome ed email dell'owner iniziale." });
    }

    if (payload.ownerEmail && !payload.ownerEmail.includes('@')) {
      errors.push({ field: 'ownerEmail', message: 'Email owner non valida.' });
    }

    if (!payload.enabledModuleKeys.length) {
      errors.push({ field: 'modules', message: 'Seleziona almeno un modulo abilitato.' });
    }

    if (payload.enabledModuleKeys.includes('staff') && !payload.ownerPhone) {
      errors.push({ field: 'ownerPhone', message: 'Compila il telefono owner per abilitare il modulo Staff.' });
    }

    if (payload.enabledModuleKeys.includes('staff') && !payload.ownerProfessionalTitle) {
      errors.push({ field: 'ownerProfessionalTitle', message: 'Compila il ruolo operativo owner per abilitare il modulo Staff.' });
    }

    return errors;
  }

  async function handleCreateTenant(event) {
    event.preventDefault();
    setCreateError('');

    const payload = {
      tenantId: createForm.tenantId.trim(),
      name: createForm.name.trim(),
      slug: createForm.slug.trim(),
      planCode: createForm.planCode.trim(),
      enabledModuleKeys: createForm.enabledModuleKeys,
      ownerFullName: createForm.ownerFullName.trim(),
      ownerEmail: createForm.ownerEmail.trim(),
      ownerPhone: createForm.ownerPhone.trim(),
      ownerProfessionalTitle: createForm.ownerProfessionalTitle.trim(),
      ownerTipoPersonale: createForm.ownerTipoPersonale,
      ownerSkills: createForm.ownerSkills
        .split(',')
        .map((skill) => skill.trim())
        .filter(Boolean),
    };
    const validationErrorsForSubmit = validateCreateTenantPayload(payload);
    if (validationErrorsForSubmit.length > 0) {
      setCreateError(validationErrorsForSubmit[0].message);
      return;
    }

    setIsCreatingTenant(true);
    const result = await createAdminTenant(payload);
    setIsCreatingTenant(false);

    if (!result.ok) {
      setCreateError(result.message);
      return;
    }

    setCreatedTenant(result.data.tenant);
    const createdTenantId = result.data.tenant.id || payload.tenantId;
    const assetsUploaded = await uploadTenantAssets(createdTenantId);
    if (!assetsUploaded) {
      setCreateError('Tenant creato, ma caricamento asset non completato.');
    }
    if (typeof refreshAdminMetrics === 'function') {
      await refreshAdminMetrics();
    }
    setCopyStatus('');
    setCreateForm(EMPTY_CREATE_FORM);
    setAssetFiles(EMPTY_ASSET_FILES);
  }

  async function copyInviteLink() {
    const inviteLink = resolveInviteLink(createdTenant);
    if (!inviteLink) {
      setCopyStatus('Link invito non disponibile.');
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopyStatus('Link invito copiato.');
    } catch {
      setCopyStatus('Copia automatica non disponibile. Seleziona e copia il link manualmente.');
    }
  }

  return (
    <section className="control-plane-page">
      <header className="control-plane-topbar">
        <div>
          <div className="control-plane-topbar__title">Crea nuovo tenant</div>
          <div className="control-plane-topbar__subtitle">
            Provisioning control plane reale con invite-based flow owner
          </div>
        </div>
        <div className="control-plane-topbar__actions">
          <Link className="control-plane-button" to={tenantsPath}>
            Torna ai tenant
          </Link>
        </div>
      </header>

      <div className="control-plane-content">
        <form onSubmit={handleCreateTenant} noValidate>
          <section className="control-plane-card">
            <div className="control-plane-card__header">
              <div>
                <div className="control-plane-card__title">1. Identità tenant</div>
                <div className="control-plane-card__subtitle">
                  Tenant ID, nome visualizzato, slug e piano del nuovo workspace.
                </div>
              </div>
            </div>
            <div className="control-plane-form-row control-plane-form-row--3">
              <label className={missingFields.has('tenant') ? 'control-plane-field is-invalid' : 'control-plane-field'}>
                <span>Tenant ID</span>
                <input
                  value={createForm.tenantId}
                  onChange={(event) => updateCreateForm('tenantId', event.target.value)}
                  placeholder="studio-rossi"
                />
              </label>
              <label className={missingFields.has('tenant') ? 'control-plane-field is-invalid' : 'control-plane-field'}>
                <span>Nome visualizzato</span>
                <input
                  value={createForm.name}
                  onChange={(event) => updateCreateForm('name', event.target.value)}
                  placeholder="Studio Rossi Advisory"
                />
              </label>
              <label className={missingFields.has('tenant') ? 'control-plane-field is-invalid' : 'control-plane-field'}>
                <span>Slug</span>
                <input
                  value={createForm.slug}
                  onChange={(event) => updateCreateForm('slug', event.target.value)}
                  placeholder="studio-rossi"
                />
              </label>
            </div>
            <div className="control-plane-form-row" style={{ marginTop: '12px' }}>
              <label className="control-plane-field">
                <span>Piano</span>
                <select
                  value={createForm.planCode}
                  onChange={(event) => updateCreateForm('planCode', event.target.value)}
                >
                  <option value="standard">standard</option>
                  <option value="pro">pro</option>
                  <option value="business">business</option>
                </select>
              </label>
            </div>
          </section>

          <section className="control-plane-card">
            <div className="control-plane-card__header">
              <div>
                <div className="control-plane-card__title">2. Moduli abilitati</div>
                <div className="control-plane-card__subtitle">
                  {enabledModuleCount} moduli selezionati.
                </div>
              </div>
            </div>
            <div className="control-plane-module-grid">
              {MODULE_OPTIONS.map((moduleOption) => {
                const isActive = createForm.enabledModuleKeys.includes(moduleOption.key);
                return (
                  <button
                    key={moduleOption.key}
                    type="button"
                    className={
                      isActive
                        ? 'control-plane-module-chip is-active'
                        : 'control-plane-module-chip'
                    }
                    onClick={() => toggleModule(moduleOption.key)}
                    aria-pressed={isActive}
                  >
                    {moduleOption.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="control-plane-card">
            <div className="control-plane-card__header">
              <div>
                <div className="control-plane-card__title">3. Owner iniziale</div>
                <div className="control-plane-card__subtitle">
                  L&apos;owner riceverà un invito di attivazione.
                </div>
              </div>
            </div>
            <div className="control-plane-form-row">
              <label className={missingFields.has('owner') ? 'control-plane-field is-invalid' : 'control-plane-field'}>
                <span>Nome completo</span>
                <input
                  value={createForm.ownerFullName}
                  onChange={(event) => updateCreateForm('ownerFullName', event.target.value)}
                  placeholder="Laura Bianchi"
                />
              </label>
              <label className={missingFields.has('owner') || missingFields.has('ownerEmail') ? 'control-plane-field is-invalid' : 'control-plane-field'}>
                <span>Email</span>
                <input
                  type="email"
                  value={createForm.ownerEmail}
                  onChange={(event) => updateCreateForm('ownerEmail', event.target.value)}
                  placeholder="owner@studio.it"
                />
              </label>
            </div>
          </section>

          <section className={isStaffModuleEnabled ? 'control-plane-card' : 'control-plane-card is-muted'}>
            <div className="control-plane-card__header">
              <div>
                <div className="control-plane-card__title">4. Dati staff owner</div>
                <div className="control-plane-card__subtitle">
                  {isStaffModuleEnabled
                    ? "Necessari per creare il primo profilo staff collegato all'owner."
                    : 'Modulo Staff non selezionato: dati operativi opzionali.'}
                </div>
              </div>
            </div>
            <div className="control-plane-form-row control-plane-form-row--3">
              <label className={missingFields.has('ownerPhone') ? 'control-plane-field is-invalid' : 'control-plane-field'}>
                <span>Telefono owner{isStaffModuleEnabled ? ' *' : ''}</span>
                <input
                  value={createForm.ownerPhone}
                  onChange={(event) => updateCreateForm('ownerPhone', event.target.value)}
                  placeholder="+39 02 1234 5678"
                />
              </label>
              <label className={missingFields.has('ownerProfessionalTitle') ? 'control-plane-field is-invalid' : 'control-plane-field'}>
                <span>Ruolo operativo{isStaffModuleEnabled ? ' *' : ''}</span>
                <input
                  value={createForm.ownerProfessionalTitle}
                  onChange={(event) => updateCreateForm('ownerProfessionalTitle', event.target.value)}
                  placeholder="Direttore operativo"
                />
              </label>
              <label className="control-plane-field">
                <span>Tipo personale</span>
                <select
                  value={createForm.ownerTipoPersonale}
                  onChange={(event) => updateCreateForm('ownerTipoPersonale', event.target.value)}
                >
                  <option value="interno">interno</option>
                  <option value="esterno">esterno</option>
                </select>
              </label>
            </div>
            <div className="control-plane-form-row" style={{ marginTop: '12px' }}>
              <label className="control-plane-field">
                <span>Competenze</span>
                <input
                  value={createForm.ownerSkills}
                  onChange={(event) => updateCreateForm('ownerSkills', event.target.value)}
                  placeholder="Leadership, Operations"
                />
              </label>
            </div>
          </section>

          <section className="control-plane-card">
            <div className="control-plane-card__header">
              <div>
                <div className="control-plane-card__title">5. Brand e template iniziali</div>
                <div className="control-plane-card__subtitle">
                  Asset opzionali caricati dopo la creazione del tenant.
                </div>
              </div>
            </div>
            <div className="control-plane-form-row control-plane-form-row--3">
              <label className="control-plane-field">
                <span>Logo azienda cliente/sidebar</span>
                <input
                  type="file"
                  accept="image/svg+xml,image/png,image/webp,image/jpeg"
                  onChange={(event) => updateAssetFile('sidebarLogo', event.target.files?.[0])}
                />
              </label>
              <label className="control-plane-field">
                <span>Template fattura</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(event) => updateAssetFile('invoiceTemplate', event.target.files?.[0])}
                />
              </label>
              <label className="control-plane-field">
                <span>Template contratto</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(event) => updateAssetFile('contractTemplate', event.target.files?.[0])}
                />
              </label>
            </div>
            <div className="control-plane-form-row" style={{ marginTop: '12px' }}>
              <label className="control-plane-field">
                <span>Template generico</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(event) => updateAssetFile('genericTemplate', event.target.files?.[0])}
                />
              </label>
            </div>
          </section>

          {createError ? <div role="alert" className="control-plane-inline-error wt-inline-error">{createError}</div> : null}
          {validationErrors.length > 0 ? (
            <div role="alert" className="control-plane-inline-error wt-inline-error" data-testid="create-tenant-validation-summary">
              {validationErrors.map((error) => error.message).join(' ')}
            </div>
          ) : null}

          <div className="control-plane-topbar__actions" style={{ marginBottom: '24px' }}>
            <button type="submit" className="control-plane-button is-primary" disabled={isCreateDisabled}>
              {isCreatingTenant ? 'Creazione in corso...' : 'Crea tenant'}
            </button>
          </div>
        </form>

        {createdTenant ? (
          <section className="control-plane-card">
            <div className="control-plane-card__header">
              <div>
                <div className="control-plane-card__title">Tenant creato</div>
                <div className="control-plane-card__subtitle">
                  Stato operativo e invite restituiti dal backend auth.
                </div>
              </div>
            </div>
            <div className="control-plane-form-row control-plane-form-row--3">
              <div className="control-plane-field">
                <span>Tenant</span>
                <input disabled value={createdTenant.name} />
              </div>
              <div className="control-plane-field">
                <span>Email owner</span>
                <input disabled value={createdTenant.ownerEmail || createForm.ownerEmail || '—'} />
              </div>
              <div className="control-plane-field">
                <span>Provisioning</span>
                <input disabled value={createdTenant.provisioningStatus || '—'} />
              </div>
            </div>
            <div className="control-plane-form-row control-plane-form-row--3" style={{ marginTop: '12px' }}>
              <div className="control-plane-field">
                <span>Owner invite</span>
                <input disabled value={createdTenant.ownerInviteStatus || 'Invito generato'} />
              </div>
              <div className="control-plane-field">
                <span>Database</span>
                <input disabled value={createdTenant.databaseName || '—'} />
              </div>
              <div className="control-plane-field">
                <span>Scadenza invito</span>
                <input disabled value={createdTenant.ownerInviteExpiresAt || 'stato invito non disponibile'} />
              </div>
            </div>
            <div className="control-plane-form-row" style={{ marginTop: '12px' }}>
              <div className="control-plane-field">
                <span>Link di attivazione owner</span>
                <input readOnly value={resolveInviteLink(createdTenant) || '—'} />
              </div>
            </div>
            <div className="control-plane-topbar__actions" style={{ marginTop: '12px', justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="control-plane-button is-primary"
                onClick={copyInviteLink}
                disabled={!resolveInviteLink(createdTenant)}
              >
                Copia link invito
              </button>
              {copyStatus ? <span className="text-sm text-[var(--text-secondary)]">{copyStatus}</span> : null}
            </div>
            <div role="alert" className="control-plane-inline-error wt-inline-error" style={{ marginTop: '12px' }}>
              Questo link è mostrato solo ora. Copialo prima di uscire.
            </div>
          </section>
        ) : null}
      </div>
    </section>
  );
}
